import { CustomerFormValues } from "@/api/types";
import {
  getCustomers,
  getCustomer,
  createCustomer as createCustomerApi,
  updateCustomer as updateCustomerApi,
  deleteCustomer as deleteCustomerApi,
} from "@/api/customer-api";
import {
  useCollectionData,
  useMutation,
  usePaginatedData,
  buildCacheKey,
} from "@/hooks/use-api";
import { Customer } from "@/types/customer-type";

// Constants
const CUSTOMERS_ENDPOINT = "/customers";

// Hook for getting all customers with pagination and search
export function useCustomers(params?: {
  page?: number;
  size?: number;
  searchTerm?: string;
}) {
  const { page = 0, size = 20, searchTerm } = params || {};
  const searchTermTrimmed = searchTerm?.trim() || "";

  const cacheKey = buildCacheKey(CUSTOMERS_ENDPOINT, {
    page,
    size,
    searchTerm: searchTermTrimmed || undefined,
  });

  return usePaginatedData(
    cacheKey,
    () => getCustomers({ page, size, searchTerm: searchTermTrimmed || null }),
    {
      refreshInterval: searchTermTrimmed ? 0 : 30000, // Don't auto-refresh search results
      revalidateOnFocus: !searchTermTrimmed, // Don't refetch on focus during search
    }
  );
}

// Hook for getting a single customer
export function useCustomer(id: string | null) {
  const cacheKey = id ? `${CUSTOMERS_ENDPOINT}/${id}` : null;

  return useCollectionData<Customer>(
    cacheKey,
    id ? () => getCustomer(id) : () => Promise.resolve({ data: null } as any),
    {
      revalidateOnFocus: false,
    }
  );
}

// Hook for creating a customer
export function useCreateCustomer() {
  return useMutation(createCustomerApi, {
    onSuccess: (response) => {
      console.log("Customer created successfully:", response.data);
    },
    onError: (error) => {
      console.error("Error creating customer:", error);
    },
    invalidateKeys: [CUSTOMERS_ENDPOINT],
  });
}

// Hook for updating a customer
export function useUpdateCustomer() {
  return useMutation(
    ({ id, data }: { id: string; data: CustomerFormValues }) =>
      updateCustomerApi(id, data),
    {
      onSuccess: (response, variables) => {
        console.log("Customer updated successfully:", response.data);
      },
      onError: (error) => {
        console.error("Error updating customer:", error);
      },
      invalidateKeys: [CUSTOMERS_ENDPOINT],
    }
  );
}

// Hook for deleting a customer
export function useDeleteCustomer() {
  return useMutation(deleteCustomerApi, {
    onSuccess: (response) => {
      console.log("Customer deleted successfully:", response.data);
    },
    onError: (error) => {
      console.error("Error deleting customer:", error);
    },
    invalidateKeys: [CUSTOMERS_ENDPOINT],
  });
}
